import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
  Chip,
  Link,
  Collapse,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import AddNotificationGroup from "./AddNotificationGroup";
import EditNotificationGroup from "./EditNotificationGroup";
import ConfirmDialog from "../ConfirmDialog";
import { formatDate } from "../../utils/formatDate";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function NotificationGroups({ onGroupSelect }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [groups, setGroups] = useState([]);
  const [query, setQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState({});

  const sortUnits = (unitsToSort) => {
    if (!sortConfig.key) return unitsToSort;

    return [...unitsToSort].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      if (typeof aValue === "string") {
        if (sortConfig.direction === "asc") return aValue.localeCompare(bValue);
        else return bValue.localeCompare(aValue);
      } else if (typeof aValue === "number") {
        if (sortConfig.direction === "asc") return aValue - bValue;
        else return bValue - aValue;
      } else {
        return 0;
      }
    });
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleGroupNameClick = (groupId) => {
    if (onGroupSelect) {
      onGroupSelect(groupId);
    }
  };

  const fetchNotificationGroups = async () => {
    try {
      setLoadingData(true);
      let Api_return = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/notificationGroups?companyId=${process.env.REACT_APP_COMPANY_ID}`,
      );

      const data = Api_return.data;
      const sortedData = sortAlphabetically(data, "name");

      setGroups(sortedData);
      setFilteredGroups(sortedData);
    } catch (err) {
      console.error("Error fetching notification groups:", err);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchNotificationGroups();
  }, []);

  const handleSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setQuery(q);
    const filtered = groups.filter((u) => {
      const name = u.name || "";
      const id = u.id || "";
      return name.toLowerCase().includes(q) || id.toLowerCase().includes(q);
    });
    setFilteredGroups(filtered);
  };

  const refreshGroups = () => {
    fetchNotificationGroups();
  };

  const addNotifGroup = () => {
    setShowAddForm(true);
  };

  const handleFormSubmit = async (newNotificationGroup) => {
    if (!newNotificationGroup) return;
    await fetchNotificationGroups();
    setShowAddForm(false);
    setQuery("");
  };

  const handleDeleteClick = (group) => {
    setSelectedGroup(group);
    setConfirmOpen(true);
  };

  const EditNotifGroup = (group) => {
    setSelectedGroup(group);
    setShowEditForm(true);
  };

  const handleEditFormSubmit = async (updatedNotificationGroup) => {
    if (!updatedNotificationGroup) return;

    setGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.id === updatedNotificationGroup.id
          ? updatedNotificationGroup
          : group,
      ),
    );

    setFilteredGroups((prevFiltered) =>
      prevFiltered.map((group) =>
        group.id === updatedNotificationGroup.id
          ? updatedNotificationGroup
          : group,
      ),
    );

    setShowEditForm(false);
    setSelectedGroup(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedGroup) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/groups/${selectedGroup.id}`,
        {
          data: {UI_Description: `${getUIDescription()}\n confirm delete notification group`, },
        },
      );

      console.log("✅ Group deleted:", selectedGroup.id);

      // Update both groups and filteredGroups by removing the deleted item
      const updatedGroups = groups.filter(
        (group) => group.id !== selectedGroup.id,
      );
      setGroups(updatedGroups);

      // Re-apply the search filter to the updated groups
      const filtered = updatedGroups.filter((u) => {
        const name = u.name || "";
        const id = u.id || "";
        return (
          name.toLowerCase().includes(query.toLowerCase()) ||
          id.toLowerCase().includes(query.toLowerCase())
        );
      });
      setFilteredGroups(filtered);
    } catch (error) {
      console.error(
        "❌ Error deleting group:",
        error.response?.data || error.message,
      );
    } finally {
      setConfirmOpen(false);
      setSelectedGroup(null);
      // Don't clear the query - keep the search active
    }
  };

  const sortedGroups = sortUnits(filteredGroups);

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 0 },
        height: "100%",
        position: "relative",
      }}
    >
      {loadingData && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontSize={{ xs: "1.15rem", sm: "2rem", md: "2rem" }}
              fontWeight="bold"
            >
              Notification Groups
            </Typography>
            <Chip
              label={`${filteredGroups.length} groups`}
              color="primary"
              size="small"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "center",
              flexWrap: "nowrap", // 👈 keep in one line
            }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 0,
                bgcolor: "#00BFFF",
                "&:hover": { bgcolor: "#18a5d4ff" },
                whiteSpace: "nowrap", // optional
                fontSize: { xs: "10.5px", sm: "15px", sm: "15px" },
                fontWeight: { xs: 600, sm: 500, sm: 500 },
              }}
              onClick={refreshGroups}
            >
              Refresh notification groups
            </Button>

            <Button
              variant="outlined"
              color="primary"
              sx={{
                borderRadius: 0,
                whiteSpace: "nowrap", // optional
                fontSize: { xs: "10.5px", sm: "15px", sm: "15px" },
                fontWeight: { xs: 600, sm: 500, sm: 500 },
              }}
              onClick={addNotifGroup}
            >
              Add notification group
            </Button>
          </Box>
        </Box>

        <TextField
          label="Search by Name or ID"
          placeholder="Search by Name or ID..."
          variant="outlined"
          value={query}
          onChange={handleSearch}
          fullWidth
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0, // ✅ remove rounding
            },
          }}
        />

        <TableContainer
          sx={{
            flexGrow: 1,
            height: { xs: "63vh", md: "63vh" },
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                {/* Mobile expand button column */}
                <TableCell
                  sx={{
                    display: { xs: "table-cell", md: "none" },
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "12px 8px",
                    width: "50px",
                  }}
                >
                  Details
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                    textAlign: "center",
                  }}
                  onClick={() => handleSort("name")}
                >
                  Name{" "}
                  {sortConfig.key === "name"
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : ""}
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textAlign: "center",
                    padding: "8px",
                  }}
                >
                  Number of Users
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textAlign: "center",
                    padding: "8px",
                  }}
                >
                  Number of Units
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Date Created
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    textAlign: "center",
                    padding: "8px",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedGroups.length > 0 ? (
                sortedGroups.map((group, index) => (
                  <React.Fragment key={group.id}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        "&:hover": {
                          backgroundColor: "#4a4d54",
                        },
                      }}
                    >
                      {/* Expand Button - Mobile Only */}
                      <TableCell
                        sx={{
                          display: { xs: "table-cell", md: "none" },
                          textAlign: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(group.id)}
                          sx={{ color: "white" }}
                        >
                          {expandedRows[group.id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        <Link
                          component="button"
                          variant="body1"
                          onClick={() => handleGroupNameClick(group.id)}
                          sx={{
                            color: "primary.main",
                            textDecoration: "none",
                            fontSize: { xs: "12px", sm: "14px", md: "15px" },
                            fontWeight: 500,
                            "&:hover": {
                              textDecoration: "underline",
                              color: "primary.dark",
                            },
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: 0,
                            textAlign: "center",
                          }}
                        >
                          {group.name}
                        </Link>
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {group.users}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {group.devices}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {group.description}
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {formatDate(group.date_created)}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {/* Mobile: Show single button that opens collapse */}
                        <Box sx={{ mt: 0.5, display: "flex", gap: 1 }}>
                          <Button
                            sx={{
                              borderRadius: 0, // ✅ remove rounding
                            }}
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => EditNotifGroup(group)}
                          >
                            Update
                          </Button>
                          <Button
                            sx={{
                              borderRadius: 0, // ✅ remove rounding
                            }}
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={() => handleDeleteClick(group)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Mobile Collapsed Row */}
                    <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={3}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        }}
                      >
                        <Collapse
                          in={expandedRows[group.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Number of Users:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {group.users}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Number of Units:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {group.devices}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Description:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {group.description}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Date Created:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {formatDate(group.date_created)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No notification groups found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AddNotificationGroup
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSubmit={handleFormSubmit}
      />

      <EditNotificationGroup
        open={showEditForm}
        onClose={() => {
          setShowEditForm(false);
          setSelectedGroup(null);
        }}
        onSubmit={handleEditFormSubmit}
        groupData={selectedGroup}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Notification Group"
        message={`Are you sure you want to delete "${selectedGroup?.name}"?`}
      />
    </Box>
  );
}
